import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Title, Text, PinInput, Image, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { showSuccessNotification } from '../../notifications';

import {
    VERIFY_LOGIN_OTP,
    REQUEST_LOGIN_OTP,
} from '../../apollo/mutations/auth';
import logo from '../../assets/cb-logo.svg';
import { useAuth } from '../../context/AuthProvider';

type Props = {
    username: string;
    onBack: () => void;
};

function LoginOtpForm({ username, onBack }: Props) {
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const [timer, setTimer] = useState(60);
    const [errorMessage, setErrorMessage] = useState('');
    const [shaking, setShaking] = useState(false);
    const [pinKey, setPinKey] = useState(0);

    const { loadTokens } = useAuth();

    const [
        verifyLoginOtp,
        { data: verifyOtpData, error: verifyOtpError, loading: verifyingOtp },
    ] = useMutation(VERIFY_LOGIN_OTP);

    const [resendOtpMutation, { loading: resendingOtp }] =
        useMutation(REQUEST_LOGIN_OTP);

    const form = useForm({
        initialValues: {
            otp: '',
        },
        validate: {
            otp: value => {
                const v = (value || '').trim();
                if (!v) {
                    return 'OTP is required';
                }
                if (!/^\d{6}$/.test(v)) {
                    return 'OTP must be 6 digits';
                }
                return null;
            },
        },
    });

    const getTimeRemaining = (date: string) => {
        const total = Date.parse(date) - Date.parse(`${new Date()}`);
        const totalSeconds = Math.floor(total / 1000);

        return {
            total,
            totalSeconds,
        };
    };

    const startTimer = (date: Date) => {
        const { total, totalSeconds } = getTimeRemaining(`${date}`);
        if (total >= 0) {
            setTimer(Math.max(0, totalSeconds));
        }
    };

    const getDeadTime = () => {
        const deadline = new Date();
        deadline.setSeconds(deadline.getSeconds() + 60);
        return deadline;
    };

    const resetTimer = () => {
        // Claer any existing timer first
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        // Reset timer display immediately
        setTimer(60);

        // new deadline and start timer
        const deadline = getDeadTime();
        const id = setInterval(() => {
            startTimer(deadline);
        }, 1000);
        timerRef.current = id;
    };

    const resendOtp = async () => {
        try {
            setErrorMessage('');
            const result = await resendOtpMutation({
                variables: {
                    input: {
                        username,
                    },
                },
            });

            if (result.data?.requestLoginOtp?.successful) {
                showSuccessNotification('OTP sent successfully');
                resetTimer();
                form.setFieldValue('otp', '');
            }
        } catch (resendError) {
            console.error('Error resending OTP:', resendError);
            setErrorMessage('Failed to resend OTP. Please try again.');
        }
    };

    const timerExpired = useMemo(() => {
        return timer <= 0;
    }, [timer]);

    const onSubmit = form.onSubmit(({ otp }) => {
        setErrorMessage('');
        verifyLoginOtp({
            variables: {
                input: {
                    otp,
                    username,
                },
            },
        });
    });

    const handleOtpChange = (value: string) => {
        form.setFieldValue('otp', value);
        if (errorMessage && value.length > 0) {
            setErrorMessage('');
        }
    };

    const handleOtpComplete = (value: string) => {
        setErrorMessage('');
        verifyLoginOtp({
            variables: {
                input: {
                    otp: value,
                    username,
                },
            },
        });
    };

    useEffect(() => {
        if (!verifyOtpError) {
            return undefined;
        }

        setErrorMessage('Invalid OTP. Please check and try again.');
        form.setFieldValue('otp', '');
        setShaking(true);
        setPinKey(k => k + 1);
        const timeout = setTimeout(() => setShaking(false), 500);
        return () => clearTimeout(timeout);
    }, [verifyOtpError]);

    useEffect(() => {
        if (verifyOtpData) {
            const { accessToken, refreshToken } = verifyOtpData.verifyLoginOtp;
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            loadTokens(true);
        }
    }, [verifyOtpData, loadTokens]);

    useEffect(() => {
        resetTimer();
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    return (
        <form onSubmit={onSubmit} className="space-y-8 mt-9 max-w-md w-[400px]">
            <style>{`
                @keyframes otp-shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
                    20%, 40%, 60%, 80% { transform: translateX(4px); }
                }
            `}</style>
            <Image src={logo} alt="Logo" width={250} />

            <div className="flex flex-col gap-3">
                <Title className="text-4xl">Enter OTP</Title>
                <Text>
                    Please enter the OTP sent via WhatsApp to {username}
                </Text>
            </div>
            {errorMessage && (
                <Alert
                    icon={<IconAlertCircle size="1rem" />}
                    title={
                        errorMessage.includes('Failed to resend')
                            ? 'Resend Failed'
                            : 'Invalid OTP'
                    }
                    color="red"
                    variant="light"
                >
                    {errorMessage}
                </Alert>
            )}
            <div
                className="flex flex-col gap-1"
                style={
                    shaking
                        ? {
                              animation: 'otp-shake 0.5s ease-in-out',
                          }
                        : undefined
                }
            >
                <PinInput
                    key={pinKey}
                    autoFocus
                    length={6}
                    oneTimeCode
                    type="number"
                    className="mx-auto"
                    radius="lg"
                    size="lg"
                    value={form.values.otp}
                    onChange={handleOtpChange}
                    onComplete={handleOtpComplete}
                />
            </div>

            <Button
                variant="subtle"
                disabled={!timerExpired || resendingOtp}
                loading={resendingOtp}
                className="text-end pt-4 text-primary mt-1 text-base font-medium h-auto flex max-w-fit px-1 ml-auto bg-transparent disabled:bg-transparent hover:bg-transparent"
                role="button"
                onClick={resendOtp}
            >
                {resendingOtp
                    ? 'Sending...'
                    : `Resend OTP${
                          !timerExpired
                              ? ` in ${timer} ${
                                    timer === 1 ? 'second' : 'seconds'
                                }`
                              : ''
                      }`}
            </Button>

            <div className="flex flex-col gap-2">
                <Button loading={verifyingOtp} type="submit" radius="xl">
                    Verify OTP & Login
                </Button>
                <Button
                    onClick={onBack}
                    variant="outline"
                    type="button"
                    radius="xl"
                >
                    Back
                </Button>
            </div>
        </form>
    );
}

export default LoginOtpForm;
